/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { Box, IconButton, MenuItem, Select } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import styled from 'styled-components';

import { ReactComponent as ArrowDownIcon } from '@/assets/icons/arrow-down.svg';
import BinIconSrc from '@/assets/icons/bin.svg';
// Removed unused eye icon imports - now using HideFieldIcon
import OverrideOffIconSrc from '@/assets/icons/override_off.svg';
import OverrideOnIconSrc from '@/assets/icons/override_on.svg';
import { SUPPORTED_CURRENCIES } from '@/constants/common';
import { getAllRoadmapNodes } from '@/features/canvas/api';
import { getBudgetDropdownsVisible } from '@/features/canvas/api/budget-dropdown-visible';
import { getBudgetPopupsVisible } from '@/features/canvas/api/budget-popup-visible';
import { getBudgetRoadmapVisible } from '@/features/canvas/api/budget-roadmap-visible';
import {
  BudgetFieldType,
  createBudgetField,
  deleteBudgetField,
  getAllBudgetsForRoadmap,
  InitiativeBudgetField,
  resetInitiativeBudget,
  updateAllInitiativesCustomFieldVisibility,
  updateBudgetField,
  updateInitiativeBudget,
} from '@/features/canvas/api/initiative_budgets';
import { BudgetCustomTextField } from '@/features/canvas/components/InitiativesAccordionContent/Fields/InitiativeBudget/BudgetCustomTextField';
import { BudgetFieldDeleteDialog } from '@/features/canvas/components/InitiativesAccordionContent/Fields/InitiativeBudget/BudgetFieldDeleteDialog';
import InitiativeBudgetFieldDropdown from '@/features/canvas/components/InitiativesAccordionContent/Fields/InitiativeBudget/InitiativeBudgetFieldDropdown';
import { LinkedHideFieldIcon } from '@/features/canvas/components/InitiativesAccordionContent/Fields/InitiativeBudget/LinkedHideFieldIcon';
import { ListStyledImage } from '@/features/canvas/components/InitiativesAccordionContent/Fields/InitiativeCustom/InitiativeCustom';
import { HideFieldIcon } from '@/features/canvas/components/InitiativesAccordionContent/HideFieldIcon';
import { SidebarListSubItem } from '@/features/canvas/components/SidebarListSubItem';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

// Helper functions
const isFieldDisabled = (budgetField: { disabled?: boolean | null }) => {
  return budgetField.disabled === true;
};

// Removed isFieldVisibleInPopup - standard fields now use HideFieldIcon + popupVisible

const getStandardFieldTitle = (fieldType: string) => {
  switch (fieldType) {
    case 'total':
      return 'Total';
    case 'capex':
      return 'CapEx';
    case 'opex':
      return 'OpEx';
    case 'spend':
      return 'Spent';
    default:
      return fieldType;
  }
};

// Helper to create InitiativeField objects for standard budget fields
const createStandardFieldInitiativeField = (
  fieldType: string,
  initiativeId: number
): components['schemas']['InitiativeField'] => {
  return {
    id: parseInt(`${initiativeId}${fieldType.length}`), // Fake numeric ID for standard fields
    title: getStandardFieldTitle(fieldType),
    type: fieldType,
    value: '',
  };
};

const formatCurrencyValue = (value: string | number) => {
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '') || '0') : value;
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numValue);
};

const parseFieldValue = (value: string): number => {
  return parseFloat(value || '0');
};

const isTotalOverride = (budgetField: InitiativeBudgetField | undefined): boolean => {
  return budgetField?.total_override === true;
};

// Format number with commas while preserving cursor position
const formatNumberWithCommas = (value: string): string => {
  // Remove all non-numeric characters except decimal
  const cleanValue = value.replace(/[^0-9.]/g, '');

  // Split by decimal point
  const parts = cleanValue.split('.');

  // Format the integer part with commas
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Join back with decimal if exists
  return parts.join('.');
};

// Get the raw number without formatting
const getRawNumber = (value: string): string => {
  return value.replace(/[^0-9.]/g, '');
};

interface InitiativeBudgetProps {
  initiativeId: number;
  isInitiativeLockedAndIsNotOwner: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  readOnly: boolean;
  field?: components['schemas']['InitiativeField'];
}

// Custom hook for budget mutations
const useBudgetMutations = (initiativeId: number, roadmapId: number) => {
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const queryClient = useQueryClient();

  // Helper function to invalidate all budget-related queries
  const invalidateBudgetQueries = () => {
    console.log('InitiativeBudget: invalidateBudgetQueries called for roadmap:', roadmapId);
    void queryClient.invalidateQueries([getAllBudgetsForRoadmap.name, roadmapId]);
    void queryClient.invalidateQueries(['initiative-budget', initiativeId]);
    void queryClient.invalidateQueries(['roadmap-budget-summary', roadmapId]);
    void queryClient.invalidateQueries(['budget-by-time-period', roadmapId]);
    void queryClient.invalidateQueries(['budget-by-section', roadmapId]);
    // Invalidate budget visibility queries
    console.log('InitiativeBudget: invalidating budget visibility queries');
    void queryClient.invalidateQueries([getBudgetPopupsVisible.name, Number(roadmapId)]);
    void queryClient.invalidateQueries([getBudgetRoadmapVisible.name, Number(roadmapId)]);
    void queryClient.invalidateQueries([getBudgetDropdownsVisible.name, Number(roadmapId)]);
  };

  const updateBudget = useMutation(
    (budgetData: Parameters<typeof updateInitiativeBudget>[0]['budgetData']) =>
      updateInitiativeBudget({
        initiativeId,
        budgetData,
        roadmapId,
      }),
    {
      onSuccess: () => {
        invalidateBudgetQueries();
        showToast('success', t('editor.sidebar.initiative.edit_field_success'));
      },
      onError: () => {
        showToast('error', t('editor.sidebar.initiative.budget.edit_field_error'));
      },
    }
  );

  const resetBudget = useMutation(() => resetInitiativeBudget({ initiativeId }), {
    onSuccess: () => {
      invalidateBudgetQueries();
      showToast('success', t('editor.sidebar.initiative.budget.reset_budget_success'));
    },
  });

  const createCustomField = useMutation(
    (fieldData: Parameters<typeof createBudgetField>[0]['fieldData']) =>
      createBudgetField({
        initiativeId,
        fieldData,
        roadmapId,
      }),
    {
      onSuccess: () => {
        invalidateBudgetQueries();
        void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmapId]);
        showToast('success', t('editor.sidebar.initiative.create_field_success'));
      },
      onError: () => {
        showToast('error', t('editor.sidebar.initiative.budget.custom_field_error'));
      },
    }
  );

  const deleteCustomField = useMutation(
    (fieldId: number) => deleteBudgetField({ initiativeId, fieldId }),
    {
      onSuccess: () => {
        invalidateBudgetQueries();
        void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmapId]);
        showToast('success', t('editor.sidebar.initiative.budget.custom_field_deleted'));
      },
      onError: () => {
        showToast('error', t('editor.sidebar.initiative.budget.delete_field_error'));
      },
    }
  );

  const updateBudgetFieldData = useMutation(
    ({
      fieldData,
      fieldId,
    }: {
      fieldData: {
        disabled?: boolean;
        popup_show?: boolean;
        text_value?: string;
        value?: number;
      };
      fieldId: number;
    }) =>
      updateBudgetField({
        initiativeId,
        fieldId,
        fieldData,
        roadmapId,
      }),
    {
      onSuccess: () => {
        invalidateBudgetQueries();
      },
    }
  );

  // Mutation for updating visibility across all initiatives (standard fields only)
  // Standard budget fields now use Initiative popupVisible system via HideFieldIcon

  // Mutation for updating custom field visibility across all initiatives
  const updateAllInitiativesCustomVisibility = useMutation(
    ({
      fieldType,
      fieldTitle,
      visibility,
    }: {
      fieldTitle: string;
      fieldType: 'big' | 'small';
      visibility: boolean;
    }) =>
      updateAllInitiativesCustomFieldVisibility({
        roadmapId,
        fieldType,
        fieldTitle,
        visibility,
      }),
    {
      onSuccess: () => {
        invalidateBudgetQueries();
      },
    }
  );

  return {
    updateBudget,
    resetBudget,
    createCustomField,
    deleteCustomField,
    updateBudgetFieldData,
    updateAllInitiativesCustomVisibility,
  };
};

// Extract field rendering logic
const BudgetFieldDisplay = ({
  budgetField,
  currentSymbol,
  editingField,
  fieldType,
  fieldValue,
  isInitiativeLockedAndIsNotOwner,
  onBlur,
  onChange,
  onClick,
  onEdit,
  onKeyDown,
  readOnly,
}: {
  budgetField: InitiativeBudgetField;
  currentSymbol: string;
  editingField: string | null;
  fieldType: BudgetFieldType;
  fieldValue: string;
  isInitiativeLockedAndIsNotOwner: boolean;
  onBlur: () => void;
  onChange: (value: string) => void;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onEdit: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  readOnly: boolean;
}) => {
  const { t } = useTranslation();
  const isEditable = budgetField.is_editable && !readOnly && !isInitiativeLockedAndIsNotOwner;
  const isOverride = fieldType === 'total' && isTotalOverride(budgetField);

  // Get the appropriate label based on field type
  const getFieldLabel = () => {
    switch (fieldType) {
      case 'total':
        return t('editor.sidebar.initiative.budget.total');
      case 'capex':
        return t('editor.sidebar.initiative.budget.capex');
      case 'opex':
        return t('editor.sidebar.initiative.budget.opex');
      case 'spend':
        return t('editor.sidebar.initiative.budget.spent');
      default:
        return budgetField.display_title;
    }
  };

  // Handle input change with formatting
  const handleFormattedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = getRawNumber(e.target.value);
    onChange(rawValue);
  };

  // Select all text on focus
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <BudgetField>
      <FieldWrapper $override={isOverride}>
        <FieldName $isTitle={fieldType === 'total'} $override={isOverride}>
          {getFieldLabel()}
        </FieldName>
        {editingField === fieldType && isEditable ? (
          <FieldInputWrapper $override={isOverride}>
            <CurrencySymbol>{currentSymbol}</CurrencySymbol>
            <StyledFieldInput
              type="text"
              value={formatNumberWithCommas(fieldValue)}
              onChange={handleFormattedChange}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onFocus={handleFocus}
              readOnly={!isEditable}
              $override={isOverride}
              autoFocus
            />
          </FieldInputWrapper>
        ) : (
          <FieldValue
            onClick={e => {
              console.log('🔴 BudgetFieldDisplay FieldValue clicked, isEditable:', isEditable);
              if (isEditable) {
                onEdit();
              } else {
                console.log('🔴 Field is not editable, calling parent onClick');
                onClick(e);
              }
            }}
            $editable={isEditable}
            $override={isOverride}
          >
            {currentSymbol}
            {formatCurrencyValue(fieldValue)}
          </FieldValue>
        )}
      </FieldWrapper>
    </BudgetField>
  );
};

const InitiativeBudget = ({
  initiativeId,
  field,
  onClick,
  readOnly,
  isInitiativeLockedAndIsNotOwner,
}: InitiativeBudgetProps) => {
  const { t } = useTranslation();
  console.log('🔴 InitiativeBudget received onClick prop:', !!onClick);

  const { roadmap } = useEditorContext();
  const defaultCurrency = SUPPORTED_CURRENCIES[0].id;
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number>(defaultCurrency);
  const [editingField, setEditingField] = useState<string | null>(null);
  // Removed fieldValues state - now reading directly from budget fields like InitiativeBudgetTab does
  // This prevents the race condition where cached state shows zeros
  const [isCreatingCustomField, setIsCreatingCustomField] = useState(false);
  const [customFieldType, setCustomFieldType] = useState<'big' | 'small' | null>(null);
  const [expandedAccordionKey, setExpandedAccordionKey] = useState<null | number>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{
    id: number;
    title: string;
    type: 'big' | 'small';
  } | null>(null);

  const { allBudgets, budgetRoadmapVisible } = useEditorContext();
  const budget = allBudgets?.find(b => b.initiative_id === initiativeId);
  const isLoading = !allBudgets;

  const {
    updateBudget,
    resetBudget,
    createCustomField,
    updateBudgetFieldData,
    updateAllInitiativesCustomVisibility,
  } = useBudgetMutations(initiativeId, roadmap?.id || 0);

  useEffect(() => {
    if (budget) {
      const currencyObj = SUPPORTED_CURRENCIES.find(c => c.currency === budget.currency);
      if (currencyObj) {
        setSelectedCurrencyId(currencyObj.id);
      }
      // No longer caching field values in state - reading directly from budget fields
    }
  }, [budget]);

  // Helper function to get current field value directly from budget
  const getCurrentFieldValue = (fieldType: BudgetFieldType): string => {
    const budgetField = budget?.fields?.find((f: InitiativeBudgetField) => f.type === fieldType);
    return budgetField?.value?.toString() || '0';
  };

  // Track editing values separately from display values
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});

  const handleInputChange = (value: string, fieldType: BudgetFieldType) => {
    // Store editing value temporarily
    setEditingValues(prev => ({ ...prev, [fieldType]: value }));
  };

  const handleCurrencyOptionChange = (value: number) => {
    setSelectedCurrencyId(value);
    const currencyCode =
      SUPPORTED_CURRENCIES.find(option => option.id === value)?.currency || 'GBP';
    updateBudget.mutate({ currency: currencyCode });
  };

  const handleBlur = (fieldType: BudgetFieldType) => {
    if (readOnly || isInitiativeLockedAndIsNotOwner) return;

    const budgetField = budget?.fields?.find((f: InitiativeBudgetField) => f.type === fieldType);
    if (!budgetField) return;

    const cleanValue = parseFieldValue(editingValues[fieldType] || getCurrentFieldValue(fieldType));

    // For spend field, always update via updateBudget
    if (fieldType === 'spend') {
      updateBudget.mutate({ spend: cleanValue });
    }
    // For total field when override is enabled
    else if (fieldType === 'total' && isTotalOverride(budgetField)) {
      updateBudget.mutate({ total_budget: cleanValue });
    }
    // For capex/opex fields
    else if (fieldType === 'capex' || fieldType === 'opex') {
      const updates: Record<string, number> = {};
      updates[fieldType] = cleanValue;
      updateBudget.mutate(updates);
    }

    setEditingField(null);
    // Clear editing value after successful save
    const newEditingValues = { ...editingValues };
    delete newEditingValues[fieldType];
    setEditingValues(newEditingValues);
  };

  const handleOverrideToggle = (totalBudgetField: InitiativeBudgetField | undefined) => {
    if (!totalBudgetField) return;

    const newOverride = !isTotalOverride(totalBudgetField);
    updateBudget.mutate({ total_budget_override: newOverride });
  };

  const handleKeyDown = (event: React.KeyboardEvent, fieldType: BudgetFieldType) => {
    if (event.key === 'Enter') {
      handleBlur(fieldType);
    }
  };

  const handleCustomFieldCreate = (title: string, textValue: string) => {
    if (!customFieldType) return;

    createCustomField.mutate({
      type: customFieldType,
      title,
      text_value: textValue,
      disabled: false,
      popup_show: true,
    });

    // Reset state after creation
    setIsCreatingCustomField(false);
    setCustomFieldType(null);
    setExpandedAccordionKey(null);
  };

  const handleCustomFieldUpdate = (fieldId: number, textValue: string) => {
    updateBudgetFieldData.mutate({
      fieldId,
      fieldData: { text_value: textValue },
    });
  };

  const handleCustomFieldDelete = (fieldId: number) => {
    // Find the field to get its title and type for the dialog
    const foundField = customFields.find(f => f.id === fieldId);
    if (foundField) {
      setFieldToDelete({
        id: fieldId,
        title: foundField.title || '',
        type: foundField.type as 'big' | 'small',
      });
      setDeleteDialogOpen(true);
    }
  };

  // UNUSED: Legacy handlers replaced by HideFieldIcon component
  /* const handleCapexOpexVisibilityToggle = (fieldType: 'capex' | 'opex') => {
    console.log('Budget visibility toggle - CapEx/OpEx:', fieldType);
    const capexField = standardFields.find((f: InitiativeBudgetField) => f.type === 'capex');
    const opexField = standardFields.find((f: InitiativeBudgetField) => f.type === 'opex');

    if (!capexField || !opexField) {
      console.warn('Missing capex or opex fields for visibility toggle');
      return;
    }

    const clickedField = fieldType === 'capex' ? capexField : opexField;
    const currentVisibility = isFieldVisibleInPopup(clickedField, popupVisible, fieldType);
    const newVisibility = !currentVisibility;

    console.log('Toggling CapEx/OpEx visibility:', {
      currentVisibility,
      newVisibility,
      fieldType,
    });

    // Update visibility for both capex and opex across all initiatives
    // Note: Now handled by HideFieldIcon component
    // updateAllInitiativesVisibility.mutate({ fieldType: 'capex', visibility: newVisibility });
    // updateAllInitiativesVisibility.mutate({ fieldType: 'opex', visibility: newVisibility });
  }; */

  /* const handleSpendVisibilityToggle = () => {
    console.log('Budget visibility toggle - Spend');
    const spendField = standardFields.find((f: InitiativeBudgetField) => f.type === 'spend');

    if (!spendField) {
      console.warn('Missing spend field for visibility toggle');
      return;
    }

    const currentVisibility = isFieldVisibleInPopup(spendField, popupVisible, 'spend');
    const newVisibility = !currentVisibility;

    console.log('Toggling Spend visibility:', {
      currentVisibility,
      newVisibility,
    });

    // Update visibility for spend across all initiatives
    // Note: Now handled by HideFieldIcon component
    // updateAllInitiativesVisibility.mutate({ fieldType: 'spend', visibility: newVisibility });
  }; */

  // For custom fields, update visibility across ALL initiatives in the roadmap
  const handleCustomFieldVisibilityToggle = (
    budgetField: InitiativeBudgetField,
    isVisible: boolean
  ) => {
    console.log('Budget field visibility toggle clicked:', {
      fieldTitle: budgetField.display_title || budgetField.title || '',
      fieldType: budgetField.type,
      newVisibility: isVisible,
    });
    updateAllInitiativesCustomVisibility.mutate({
      fieldType: budgetField.type as 'big' | 'small',
      fieldTitle: budgetField.display_title || budgetField.title || '',
      visibility: isVisible,
    });
  };

  if (isLoading) {
    return <LoadingWrapper>{t('editor.sidebar.initiative.budget.loading_budget')}</LoadingWrapper>;
  }

  if (!budget?.fields || budget.fields.length === 0) {
    return <LoadingWrapper>{t('editor.sidebar.initiative.budget.no_budget')}</LoadingWrapper>;
  }

  const currentSymbol = SUPPORTED_CURRENCIES.find(c => c.id === selectedCurrencyId)?.symbol || '£';
  const standardFields = budget.fields.filter((f: InitiativeBudgetField) =>
    ['total', 'capex', 'opex', 'spend'].includes(f.type)
  );
  // Only show custom fields that are in budgetRoadmapVisible (active in roadmap)
  // This prevents imported fields from appearing in sidebar if they're only in dropdown
  // BUT we must show ALL fields that exist in budgetRoadmapVisible, regardless of popup_show
  const customFields = budget.fields.filter((f: InitiativeBudgetField) => {
    if (!['big', 'small'].includes(f.type)) return false;

    // Check if field exists in budgetRoadmapVisible (active fields list)
    // This is independent of popup_show which only controls eye icon state
    const isActiveInRoadmap = budgetRoadmapVisible.some(
      roadmapField => roadmapField.title === f.display_title && roadmapField.type === f.type
    );

    return isActiveInRoadmap;
  });

  const totalField = standardFields.find((f: InitiativeBudgetField) => f.type === 'total');
  const capexField = standardFields.find((f: InitiativeBudgetField) => f.type === 'capex');
  const opexField = standardFields.find((f: InitiativeBudgetField) => f.type === 'opex');
  const spendField = standardFields.find((f: InitiativeBudgetField) => f.type === 'spend');

  return (
    <BudgetContainer
      onClick={e => {
        console.log('🔴 BudgetContainer clicked');
        onClick(e);
      }}
    >
      <HeaderActions>
        <StyledSelect
          readOnly={readOnly || isInitiativeLockedAndIsNotOwner}
          value={selectedCurrencyId}
          onChange={e => handleCurrencyOptionChange(e.target.value as number)}
          SelectDisplayProps={{
            style: {
              backgroundColor: 'none',
              padding: '0.5rem 1rem',
            },
          }}
          MenuProps={{
            style: {
              width: '100%',
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
          variant="standard"
          disableUnderline
          displayEmpty
          IconComponent={() => <ArrowDownIcon />}
          renderValue={selected => {
            const option = SUPPORTED_CURRENCIES.find(currency => currency.id === selected);
            return option?.currency || 'GBP';
          }}
        >
          {SUPPORTED_CURRENCIES.map(option => (
            <StyledMenuItem key={option.id} value={option.id}>
              {option.currency}
            </StyledMenuItem>
          ))}
        </StyledSelect>
        {!readOnly && (
          <ActionButtons>
            {field && <HideFieldIcon initiativeField={field} />}
            {!isInitiativeLockedAndIsNotOwner && (
              <IconButton aria-label="delete" size="small" onClick={() => resetBudget.mutate()}>
                <Tooltip title={t('editor.sidebar.initiative.delete_field')} placement="top">
                  <ListStyledImage src={BinIconSrc} />
                </Tooltip>
              </IconButton>
            )}
          </ActionButtons>
        )}
      </HeaderActions>

      <BudgetDetails>
        <BudgetFieldsColumn>
          {/* Total Field with Override Button - Always show if not disabled */}
          {totalField && !isFieldDisabled(totalField) && (
            <BudgetFieldDisplay
              budgetField={totalField}
              fieldType="total"
              fieldValue={
                editingField === 'total'
                  ? editingValues.total || getCurrentFieldValue('total')
                  : getCurrentFieldValue('total')
              }
              currentSymbol={currentSymbol}
              editingField={editingField}
              readOnly={readOnly || !isTotalOverride(totalField)}
              isInitiativeLockedAndIsNotOwner={isInitiativeLockedAndIsNotOwner}
              onEdit={() => setEditingField('total')}
              onChange={value => handleInputChange(value, 'total')}
              onBlur={() => handleBlur('total')}
              onKeyDown={e => handleKeyDown(e, 'total')}
              onClick={onClick}
            />
          )}

          {/* CapEx Field - Always show if not disabled */}
          {capexField && !isFieldDisabled(capexField) && (
            <BudgetFieldDisplay
              budgetField={capexField}
              fieldType="capex"
              fieldValue={
                editingField === 'capex'
                  ? editingValues.capex || getCurrentFieldValue('capex')
                  : getCurrentFieldValue('capex')
              }
              currentSymbol={currentSymbol}
              editingField={editingField}
              readOnly={readOnly}
              isInitiativeLockedAndIsNotOwner={isInitiativeLockedAndIsNotOwner}
              onEdit={() => setEditingField('capex')}
              onChange={value => handleInputChange(value, 'capex')}
              onBlur={() => handleBlur('capex')}
              onKeyDown={e => handleKeyDown(e, 'capex')}
              onClick={onClick}
            />
          )}

          {/* OpEx Field - Always show if not disabled */}
          {opexField && !isFieldDisabled(opexField) && (
            <BudgetFieldDisplay
              budgetField={opexField}
              fieldType="opex"
              fieldValue={
                editingField === 'opex'
                  ? editingValues.opex || getCurrentFieldValue('opex')
                  : getCurrentFieldValue('opex')
              }
              currentSymbol={currentSymbol}
              editingField={editingField}
              readOnly={readOnly}
              isInitiativeLockedAndIsNotOwner={isInitiativeLockedAndIsNotOwner}
              onEdit={() => setEditingField('opex')}
              onChange={value => handleInputChange(value, 'opex')}
              onBlur={() => handleBlur('opex')}
              onKeyDown={e => handleKeyDown(e, 'opex')}
              onClick={onClick}
            />
          )}

          {/* Show separator if any budget field above exists and spend field exists */}
          {((totalField && !isFieldDisabled(totalField)) ||
            (capexField && !isFieldDisabled(capexField)) ||
            (opexField && !isFieldDisabled(opexField))) &&
            spendField &&
            !isFieldDisabled(spendField) && <Separator />}

          {/* Spend Field - Always show if not disabled */}
          {spendField && !isFieldDisabled(spendField) && (
            <BudgetFieldDisplay
              budgetField={spendField}
              fieldType="spend"
              fieldValue={
                editingField === 'spend'
                  ? editingValues.spend || getCurrentFieldValue('spend')
                  : getCurrentFieldValue('spend')
              }
              currentSymbol={currentSymbol}
              editingField={editingField}
              readOnly={readOnly}
              isInitiativeLockedAndIsNotOwner={isInitiativeLockedAndIsNotOwner}
              onEdit={() => setEditingField('spend')}
              onChange={value => handleInputChange(value, 'spend')}
              onBlur={() => handleBlur('spend')}
              onKeyDown={e => handleKeyDown(e, 'spend')}
              onClick={onClick}
            />
          )}

          {/* Show separator if any standard field exists and there are custom fields */}
          {((totalField && !isFieldDisabled(totalField)) ||
            (capexField && !isFieldDisabled(capexField)) ||
            (opexField && !isFieldDisabled(opexField)) ||
            (spendField && !isFieldDisabled(spendField))) &&
            customFields.length > 0 && <Separator />}

          {/* Custom Fields */}
          {customFields.map((customField: InitiativeBudgetField) => (
            <SidebarListSubItem
              key={customField.id}
              id={customField.id}
              ariaId={customField.id.toString()}
              title={customField.display_title}
              isExpanded={expandedAccordionKey === customField.id}
              setExpandedAccordionKey={setExpandedAccordionKey}
            >
              <Box>
                <BudgetCustomTextField
                  field={customField}
                  readOnly={readOnly}
                  isInitiativeLockedAndIsNotOwner={isInitiativeLockedAndIsNotOwner}
                  onUpdate={handleCustomFieldUpdate}
                  onDelete={handleCustomFieldDelete}
                  onToggleVisibility={handleCustomFieldVisibilityToggle}
                  isLoading={false}
                />
              </Box>
            </SidebarListSubItem>
          ))}

          {/* New custom field in creation */}
          {isCreatingCustomField && customFieldType && (
            <SidebarListSubItem
              key="temporary-budget-custom-field"
              id={0}
              ariaId="temporary-budget-custom-field"
              title={t('editor.sidebar.initiative.budget.custom_field')}
              isExpanded={expandedAccordionKey === 0}
              setExpandedAccordionKey={setExpandedAccordionKey}
            >
              <Box>
                <BudgetCustomTextField
                  field={{
                    id: 0,
                    initiative_budget_id: 0,
                    type: customFieldType as BudgetFieldType,
                    display_title: '',
                    value: 0,
                    text_value: '',
                    formatted_value: '',
                    is_editable: true,
                    is_visible: true,
                    popup_show: true,
                    created_at: '',
                    updated_at: '',
                    title: '',
                  }}
                  isInCreationMode
                  readOnly={readOnly}
                  isInitiativeLockedAndIsNotOwner={isInitiativeLockedAndIsNotOwner}
                  onUpdate={() => {}}
                  onDelete={() => {}}
                  onToggleVisibility={() => {}}
                  onCreateSubmit={handleCustomFieldCreate}
                  isLoading={createCustomField.isLoading}
                />
              </Box>
            </SidebarListSubItem>
          )}

          {/* Add custom field dropdown */}
          {!readOnly && !isInitiativeLockedAndIsNotOwner && !isCreatingCustomField && (
            <InitiativeBudgetFieldDropdown
              disabled={false}
              initiativeId={initiativeId}
              setIsCreatingCustomField={setIsCreatingCustomField}
              setCustomFieldType={setCustomFieldType}
              setExpandedAccordionKey={setExpandedAccordionKey}
            />
          )}
        </BudgetFieldsColumn>

        {/* Icons column - absolutely positioned to align with eye icon */}
        <IconsColumn>
          {/* Total field icon (override) - always show */}
          {totalField && !isFieldDisabled(totalField) && (
            <IconsWrapper>
              <IconButton
                size="small"
                onClick={() => handleOverrideToggle(totalField)}
                style={{
                  padding: '5px',
                  backgroundColor: isTotalOverride(totalField) ? '#363648' : 'transparent',
                  borderRadius: '6px',
                }}
              >
                <Tooltip title={t('editor.sidebar.initiative.budget.override')} placement="top">
                  <ListStyledImage
                    src={isTotalOverride(totalField) ? OverrideOnIconSrc : OverrideOffIconSrc}
                    style={{
                      filter: isTotalOverride(totalField) ? 'brightness(0) invert(1)' : 'none',
                    }}
                  />
                </Tooltip>
              </IconButton>
            </IconsWrapper>
          )}

          {/* CapEx/OpEx linked icon - show linked toggle for both fields */}
          {capexField &&
            opexField &&
            !isFieldDisabled(capexField) &&
            !isFieldDisabled(opexField) &&
            !readOnly && (
              <IconsWrapper>
                <LinkedHideFieldIcon
                  capexField={createStandardFieldInitiativeField('capex', initiativeId)}
                  opexField={createStandardFieldInitiativeField('opex', initiativeId)}
                />
              </IconsWrapper>
            )}

          {/* OpEx icon - show same linked icon as CapEx */}
          {opexField && !isFieldDisabled(opexField) && !readOnly && (
            <IconsWrapper>
              <LinkedHideFieldIcon
                capexField={createStandardFieldInitiativeField('capex', initiativeId)}
                opexField={createStandardFieldInitiativeField('opex', initiativeId)}
              />
            </IconsWrapper>
          )}

          {/* Empty div for separator spacing */}
          <div style={{ height: '9px' }} />

          {/* Spend icon - always visible to allow toggling */}
          {spendField && !isFieldDisabled(spendField) && !readOnly && (
            <IconsWrapper>
              <HideFieldIcon
                initiativeField={createStandardFieldInitiativeField('spend', initiativeId)}
              />
            </IconsWrapper>
          )}
        </IconsColumn>
      </BudgetDetails>

      {/* Budget Field Delete Dialog */}
      <BudgetFieldDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setFieldToDelete(null);
        }}
        fieldId={fieldToDelete?.id || null}
        fieldTitle={fieldToDelete?.title || ''}
        fieldType={fieldToDelete?.type || 'big'}
        initiativeId={initiativeId}
      />
    </BudgetContainer>
  );
};

export { InitiativeBudget };

// Styled components
const BudgetContainer = styled.div`
  width: 100%;
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`;

const StyledSelect = styled(Select)`
  border-radius: 0.5rem;
  font-size: 1.25rem;
  font-style: italic;
  max-width: 63px;
  border: 0;
  background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  svg {
    width: 2rem;
    height: 2rem;
    position: relative;
    right: 1rem;
    transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    pointer-events: none;
  }
  & .MuiInput-input:focus {
    border-radius: 1rem;
    background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 1.25rem;
`;

const BudgetDetails = styled.div`
  margin-top: 5px;
  position: relative;
`;

const BudgetFieldsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const IconsColumn = styled.div`
  position: absolute;
  right: 24.5px;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 28px;
  align-items: center;
`;

const BudgetField = styled.div`
  display: flex;
  align-items: center;
  border-radius: 0.5rem;
`;

const FieldWrapper = styled.div<{ $override?: boolean }>`
  display: flex;
  align-items: center;
`;

const FieldName = styled.div<{ $isTitle?: boolean; $override?: boolean }>`
  font-size: 14px;
  color: ${({ $override, theme }) =>
    $override ? theme.palette.brand.white : theme.palette.brand.textPrimary};
  min-width: 63px;
  height: 26px;
  display: flex;
  align-items: center;
  background-color: ${({ $override, theme }) =>
    $override ? '#363648' : theme.palette.brand.backgroundDialog};
  padding: 0.5rem 1rem;
  border: 1px solid
    ${({ $override, theme }) => ($override ? '#363648' : theme.palette.brand.backgroundDialog)};
  border-radius: 0.5rem 0 0 0.5rem;
  transition: all 0.2s ease-in-out;
  box-sizing: border-box;
`;

const FieldValue = styled.div<{ $editable?: boolean; $override?: boolean }>`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
  background-color: ${({ $override }) => ($override ? 'white' : 'transparent')};
  font-style: italic;
  font-weight: 500;
  min-width: 100px;
  max-width: 100px;
  height: 26px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border: 1px solid
    ${({ $override, theme }) => ($override ? '#363648' : theme.palette.brand.backgroundDialog)};
  border-left: 0;
  border-radius: 0 0.5rem 0.5rem 0;
  cursor: ${({ $editable }) => ($editable ? 'text' : 'default')};
  transition: all 0.2s ease-in-out;
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
`;

const FieldInputWrapper = styled.div<{ $override?: boolean }>`
  display: flex;
  align-items: center;
  background-color: white;
  min-width: 100px;
  max-width: 100px;
  height: 26px;
  border: 1px solid
    ${({ $override, theme }) =>
      $override ? theme.palette.brand.textPrimary : theme.palette.brand.backgroundDialog};
  border-left: 0;
  border-radius: 0 0.5rem 0.5rem 0;
  box-sizing: border-box;
  overflow: hidden;
`;

const CurrencySymbol = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
  font-weight: 500;
  font-style: italic;
  padding-left: 1rem;
  flex-shrink: 0;
`;

const StyledFieldInput = styled.input<{ $override?: boolean }>`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
  background-color: transparent;
  font-weight: 500;
  font-style: italic;
  height: 100%;
  outline: none;
  padding: 0 1rem 0 0.25rem;
  border: none;
  box-sizing: border-box;
  width: 100%;

  &:read-only {
    cursor: default;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  justify-content: center;
  height: 26px; /* Match the field height */
`;

const LoadingWrapper = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  margin: 0.25rem 0;
`;
